import SqFetch from 'typescript/application/sq_fetch';
import { _handleResponse } from 'typescript/clients/utilities/handle_http_response';
import { getCountryCode, getLanguageCode } from 'typescript/utilities/locales';

// functions are controlled by the methods in this controller
// app/controllers/sq/api/carts_controller.rb

export const CART_BASE_URL = '/shop/api/cart';
export type UpdateCartAction = 'add' | 'overwrite' | 'subtract';

export interface LineItemAttributes {
  variant_token: string;
  quantity: number;
  add_ons?: string[];
}

export interface UpdateCartRequest {
  update_cart_action: UpdateCartAction;
  line_items_attributes: LineItemAttributes[];
  store_code: string;
  language_code: string;
  location_id?: string;
  search_postal_code?: string;
}

export interface UpdateCartResponse {
  subtotal: number;
  item_count: number;
}

/**
 * Update cart quantities for multiple line items in a single request
 * @param lineItems - line item attributes (variant token + target quantity)
 * @param action - the cart action applied to every line item
 * @param bopisLocationId - optional BOPIS location ID
 * @param bopisSearchPostalCode - optional BOPIS search postal code
 * @returns response with success status and cart data
 */
export const updateLineItemQuantities = async ({
  lineItems,
  action,
  bopisLocationId,
  bopisSearchPostalCode,
}: {
  lineItems: LineItemAttributes[];
  action: UpdateCartAction;
  bopisLocationId?: string;
  bopisSearchPostalCode?: string;
}): Promise<UpdateCartResponse> => {
  const requestBody: UpdateCartRequest = {
    update_cart_action: action,
    store_code: getCountryCode(),
    language_code: getLanguageCode(),
    line_items_attributes: lineItems,
  };

  // Add BOPIS parameters if both are provided
  if (bopisLocationId && bopisSearchPostalCode) {
    requestBody.location_id = bopisLocationId;
    requestBody.search_postal_code = bopisSearchPostalCode;
  }

  const response = await SqFetch.put(CART_BASE_URL, requestBody);

  return _handleResponse(response);
};

/**
 * Update cart quantity for a line item
 * @param variantToken - the product variant token
 * @param quantity - the quantity to set
 * @param addOnIds - optional array of add-on IDs
 * @param action - the cart action (add, update, remove)
 * @param bopisLocationId - optional BOPIS location ID
 * @param bopisSearchPostalCode - optional BOPIS search postal code
 * @returns response with success status and cart data
 */
export const updateLineItemQuantity = ({
  variantToken,
  quantity,
  addOnIds,
  action,
  bopisLocationId,
  bopisSearchPostalCode,
}: {
  variantToken: string;
  quantity: number;
  addOnIds?: string[];
  action: UpdateCartAction;
  bopisLocationId?: string;
  bopisSearchPostalCode?: string;
}): Promise<UpdateCartResponse> =>
  updateLineItemQuantities({
    lineItems: [
      {
        variant_token: variantToken,
        quantity,
        add_ons: addOnIds,
      },
    ],
    action,
    bopisLocationId,
    bopisSearchPostalCode,
  });
